import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Clock,
  Save,
  Trash2,
  X as XIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import {
  addUserNote,
  deleteUser,
  getUserDetails,
  updateUserStatus,
} from '../api/services';
import {
  formatDate,
  getApiErrorMessage,
  getDisplayName,
  getInitials,
  slugToLabel,
} from '../utils/admin';

const STATUS_ACTIONS = {
  accepted: {
    label: 'Approve',
    buttonClass: 'bg-green-600 hover:bg-green-700',
    icon: Check,
  },
  rejected: {
    label: 'Reject',
    buttonClass: 'bg-red-600 hover:bg-red-700',
    icon: XIcon,
  },
  waitlisted: {
    label: 'Waitlist',
    buttonClass: 'bg-orange-500 hover:bg-orange-600',
    icon: Clock,
  },
};

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: admin } = useAuth();

  const [user, setUser] = useState(null);
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    status: '',
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [newNote, setNewNote] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  const fetchUserDetails = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getUserDetails(userId);
      setUser(response.user || null);
      setVerificationHistory(response.verificationHistory || []);
    } catch (fetchError) {
      console.error('Error loading user details:', fetchError);
      setError(getApiErrorMessage(fetchError, 'Failed to load user details'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const openStatusModal = (status) => {
    setStatusModal({
      isOpen: true,
      status,
    });
    setActionReason('');
  };

  const closeStatusModal = () => {
    setStatusModal({
      isOpen: false,
      status: '',
    });
    setActionReason('');
  };

  const handleStatusUpdate = async () => {
    if (!statusModal.status) {
      return;
    }

    try {
      const response = await updateUserStatus(
        userId,
        statusModal.status,
        actionReason.trim() || undefined
      );
      toast.success(
        response.message || `User status updated to ${slugToLabel(statusModal.status)}`
      );
      closeStatusModal();
      fetchUserDetails();
    } catch (updateError) {
      toast.error(getApiErrorMessage(updateError, 'Failed to update user status'));
    }
  };

  const handleAddNote = async (event) => {
    event.preventDefault();
    if (!newNote.trim()) {
      return;
    }

    setNoteSubmitting(true);

    try {
      const response = await addUserNote(userId, newNote.trim());
      toast.success(response.message || 'Note added successfully');
      setNewNote('');
      fetchUserDetails();
    } catch (noteError) {
      toast.error(getApiErrorMessage(noteError, 'Failed to add note'));
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteUser(userId);
      toast.success(response.message || 'User deleted successfully');
      navigate('/users');
    } catch (deleteError) {
      toast.error(getApiErrorMessage(deleteError, 'Failed to delete user'));
    }
  };

  const renderNote = (note, index) => {
    if (typeof note === 'string') {
      return (
        <div key={`${note}-${index}`} className="rounded-lg bg-white p-3 text-sm shadow-sm">
          <p>{note}</p>
        </div>
      );
    }

    return (
      <div key={note._id || note.createdAt || index} className="rounded-lg bg-white p-3 text-sm shadow-sm">
        <p>{note.content || note.note || note.notes || 'Note added'}</p>
        {note.createdAt && (
          <span className="mt-1 block text-xs text-gray-400">
            {formatDate(note.createdAt)}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg bg-red-50 p-4 text-red-700">{error}</div>;
  }

  if (!user) {
    return <div className="rounded-lg bg-white p-8 text-center text-gray-500">User not found.</div>;
  }

  const displayName = getDisplayName(user, 'Unknown user');
  const statusMeta = STATUS_ACTIONS[user.verificationStatus] || {};
  const eventsRegisteredCount = user.eventsRegistered?.length || 0;
  const eventsAttendedCount = user.eventsAttended?.length || 0;
  const eventsWaitlistedCount = user.eventsWaitlisted?.length || 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <button
        onClick={() => navigate('/users')}
        className="flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} className="mr-1" /> Back to Users
      </button>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            {user.profilePhotoUrl ? (
              <img
                src={user.profilePhotoUrl}
                alt={displayName}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 text-xl font-semibold text-gray-600">
                {getInitials(user, 'U')}
              </div>
            )}

            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                <span>{user.phoneNumber}</span>
                {user.email && (
                  <>
                    <span>&bull;</span>
                    <span>{user.email}</span>
                  </>
                )}
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    user.verificationStatus === 'accepted'
                      ? 'bg-green-100 text-green-800'
                      : user.verificationStatus === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : user.verificationStatus === 'waitlisted'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {slugToLabel(user.verificationStatus)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.entries(STATUS_ACTIONS)
              .filter(([status]) => status !== user.verificationStatus)
              .map(([status, config]) => {
                const ActionIcon = config.icon;
                return (
                  <button
                    key={status}
                    onClick={() => openStatusModal(status)}
                    className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-white ${config.buttonClass}`}
                  >
                    <ActionIcon size={16} className="mr-2" /> {config.label}
                  </button>
                );
              })}

            {admin?.role === 'super_admin' && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                <Trash2 size={16} className="mr-2" /> Delete User
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 p-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6">
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Age</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{user.age || 'N/A'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Profession</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{user.profession || 'N/A'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">City</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{user.city || 'N/A'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Phone Verified</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{user.phoneVerified ? 'Yes' : 'No'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Companion Profile</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{user.isCompanion ? 'Yes' : 'No'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Joined</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{formatDate(user.createdAt)}</p>
              </div>
            </section>

            <section className="rounded-xl border border-gray-100 p-5">
              <h2 className="text-lg font-semibold text-gray-900">Activity Summary</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div className="rounded-lg bg-blue-50 p-4">
                  <p className="text-sm text-blue-700">Events Registered</p>
                  <p className="mt-2 text-2xl font-bold text-blue-900">{eventsRegisteredCount}</p>
                </div>
                <div className="rounded-lg bg-green-50 p-4">
                  <p className="text-sm text-green-700">Events Attended</p>
                  <p className="mt-2 text-2xl font-bold text-green-900">{eventsAttendedCount}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-4">
                  <p className="text-sm text-orange-700">Events Waitlisted</p>
                  <p className="mt-2 text-2xl font-bold text-orange-900">{eventsWaitlistedCount}</p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-gray-100 p-5">
              <h2 className="text-lg font-semibold text-gray-900">Profile Metadata</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Preferred Categories</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {user.preferredCategories?.length ? (
                      user.preferredCategories.map((category) => (
                        <span
                          key={category}
                          className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
                        >
                          {slugToLabel(category)}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No preferred categories set.</span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Social Profiles</p>
                  <div className="mt-2 space-y-2">
                    {user.socialProfiles?.length ? (
                      user.socialProfiles.map((profile) => (
                        <a
                          key={profile}
                          href={profile}
                          target="_blank"
                          rel="noreferrer"
                          className="block break-all text-sm text-blue-600 hover:text-blue-800"
                        >
                          {profile}
                        </a>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No social profiles added.</span>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-gray-100 p-5">
              <h2 className="text-lg font-semibold text-gray-900">Verification History</h2>
              <div className="mt-4 space-y-3">
                {verificationHistory.length ? (
                  verificationHistory.map((entry, index) => (
                    <div
                      key={`${entry.createdAt || 'history'}-${index}`}
                      className="rounded-lg bg-gray-50 p-4"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium text-gray-900">
                          {slugToLabel(entry.previousStatus)} to {slugToLabel(entry.newStatus)}
                        </span>
                        <span>&bull;</span>
                        <span>{formatDate(entry.createdAt)}</span>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">
                        Changed by:{' '}
                        <span className="font-medium text-gray-800">
                          {getDisplayName(
                            entry.changedBy,
                            typeof entry.changedBy === 'string'
                              ? entry.changedBy
                              : 'Unknown admin'
                          )}
                        </span>
                      </p>
                      {entry.reason && (
                        <p className="mt-2 text-sm text-gray-600">Reason: {entry.reason}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No verification history available.</p>
                )}
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <section className="rounded-xl bg-gray-50 p-5">
              <h2 className="text-lg font-semibold text-gray-900">Internal Notes</h2>

              <div className="mt-4 space-y-3">
                {user.notes?.length ? (
                  user.notes.map((note, index) => renderNote(note, index))
                ) : (
                  <p className="text-sm text-gray-500">No notes added yet.</p>
                )}
              </div>

              <form onSubmit={handleAddNote} className="mt-4 space-y-3">
                <textarea
                  value={newNote}
                  onChange={(event) => setNewNote(event.target.value)}
                  placeholder="Add an internal note"
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={noteSubmitting || !newNote.trim()}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save size={16} className="mr-2" /> Save Note
                </button>
              </form>
            </section>

            <section className="rounded-xl border border-gray-100 p-5">
              <h2 className="text-lg font-semibold text-gray-900">Current Status</h2>
              <p className="mt-3 text-sm text-gray-600">
                The user is currently{' '}
                <span className="font-medium text-gray-900">
                  {slugToLabel(user.verificationStatus)}
                </span>
                .
              </p>
              {statusMeta.label && (
                <p className="mt-2 text-sm text-gray-500">
                  This state can be changed from the action buttons above.
                </p>
              )}
            </section>
          </div>
        </div>
      </div>

      <Modal
        isOpen={statusModal.isOpen}
        onClose={closeStatusModal}
        title={`${slugToLabel(statusModal.status)} User`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Update <span className="font-medium text-gray-900">{displayName}</span> to{' '}
            <span className="font-medium text-gray-900">
              {slugToLabel(statusModal.status)}
            </span>
            .
          </p>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Reason (optional)
            </label>
            <textarea
              value={actionReason}
              onChange={(event) => setActionReason(event.target.value)}
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add context for this decision"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={closeStatusModal}
              className="rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleStatusUpdate}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
                STATUS_ACTIONS[statusModal.status]?.buttonClass || 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will soft-delete <span className="font-medium text-gray-900">{displayName}</span>{' '}
            and revoke all refresh tokens.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteModalOpen(false)}
              className="rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Delete User
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserDetails;
